import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Select } from '@/components/Select';
import { Combobox } from '@/components/Combobox';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { MultiSelect } from '@/components/MultiSelect';
import { getOffers, createOffer, updateOffer, deleteOffer } from '@/api/offers';
import { listUsers } from '@/api/users';
import { getFunnelAudit, updateFunnelAudit } from '@/api/funnel-audits';
import { getApps } from '@/api/apps';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getOfferPoolCampaigns, syncOfferPoolCampaigns } from '@/api/tracker-offer-pools';
import { getFlows, getFlowOffers, createFlow, attachOffer } from '@/api/tracker-flows';
import type { Offer, CreateOfferInput, UpdateOfferInput } from '@/api/types';
import { COUNTRIES, COUNTRY_MAP } from '@/lib/countries';
import { ExportModal } from '@/components/offers/ExportModal';
import { TrafficGeoGapsModal } from '@/components/offers/TrafficGeoGapsModal';
import { GeoCoverageModal } from '@/components/offers/GeoCoverageModal';
import { PoolCoverageModal } from '@/components/offers/PoolCoverageModal';
import { useAuthStore } from '@/store/auth';
import { formatDateTime, parseUtc } from '@/lib/datetime';
import { VERTICAL_BADGE, VERTICAL_OPTIONS, verticalLabel, type Vertical } from '@/lib/vertical';

type ModalMode = 'create' | 'edit' | 'delete' | 'missing-geo' | 'geo-coverage' | 'traffic-geo-gaps' | 'distribute' | 'pool-coverage' | null;

type SortKey =
  | 'geo_code'
  | 'geo_name'
  | 'partner'
  | 'brand'
  | 'traffic_source'
  | 'vertical'
  | 'payout'
  | 'rs'
  | 'mindep_bl'
  | 'cap'
  | 'kpi_text'
  | 'wager'
  | 'kpi'
  | 'created_by'
  | 'created_at'
  | 'clicks'
  | 'regs'
  | 'deps'
  | 'r2d'
  | 'status';

interface Filters {
  search: string;
  partners: string[];
  brands: string[];
  trafficSources: string[];
  verticals: string[];
  currencies: string[];
  statuses: string[];
  geoCodes: string[];
  hasKpi: 'all' | 'yes' | 'no';
  capState: 'all' | 'none' | 'in_progress' | 'reached';
}

const initialFilters: Filters = {
  search: '',
  partners: [],
  brands: [],
  trafficSources: [],
  verticals: [],
  currencies: [],
  statuses: [],
  geoCodes: [],
  hasKpi: 'all',
  capState: 'all',
};

interface DistributeReport {
  offersConsidered: number;
  offersSkippedMultiGeo: number;
  campaignsProcessed: number;
  flowsCreated: number;
  offersAttached: number;
  offersAlreadyPresent: number;
  errors: string[];
}

interface FormData {
  partner: string;
  geo: string;
  brand: string;
  payout: string;
  currency: 'USD' | 'EUR';
  lang: string;
  description: string;
  contact: string;
  offer_url: string;
  cap: string;
  wager: string;
  kpiText: string;
  mindepBl: string;
  rs: string;
  trafficSource: string;
  vertical: '' | Vertical;
  dealModel: 'cpa' | 'spend';
  budget: string;
  payType: '' | 'prepay' | 'postpay';
  reportUrl: string;
  sharedCap: boolean;
  sharedOfferIds: string[];
  resetCapDeps: boolean;
  status: 'active' | 'paused';
  poolCampaignIds: string[];
  responsibleAdminId: string;
}

const initialFormData: FormData = {
  partner: '',
  geo: '',
  brand: '',
  payout: '',
  currency: 'USD',
  lang: '',
  description: '',
  contact: '',
  offer_url: '',
  cap: '',
  wager: '',
  kpiText: '',
  mindepBl: '',
  rs: '',
  trafficSource: '',
  vertical: '',
  dealModel: 'cpa',
  budget: '',
  payType: '',
  reportUrl: '',
  sharedCap: false,
  sharedOfferIds: [],
  resetCapDeps: false,
  status: 'active',
  poolCampaignIds: [],
  responsibleAdminId: '',
};

const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', EUR: '€' };
const fmtPayout = (cents: number | null | undefined, currency: string | undefined) => {
  if (cents == null) return '—';
  const sym = CURRENCY_SYMBOLS[currency || 'USD'] || '$';
  return `${sym}${(cents / 100).toFixed(2)}`;
};

function offerAge(createdAt: string): string {
  const created = parseUtc(createdAt);
  if (!created) return '—';
  const hours = Math.max(0, Math.floor((Date.now() - created.getTime()) / (60 * 60 * 1000)));
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 24) return `${months}mo`;
  return `${Math.floor(months / 12)}y ${months % 12}mo`;
}

const offerR2d = (offer: Offer): number =>
  offer.regs > 0 ? (offer.deps / offer.regs) * 100 : 0;


function collectUniqueSorted(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const v = (raw || '').trim();
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return out;
}

export function Offers() {
  const queryClient = useQueryClient();
  const currentAdmin = useAuthStore((s) => s.admin);
  const isSuperAdmin = currentAdmin?.role === 'super_admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceAuditId = searchParams.get('from_audit') ?? '';
  const prefilledAuditId = useRef('');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  // Offer the current create form was copied from — only drives the hint banner.
  const [duplicateSource, setDuplicateSource] = useState<Offer | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState('');
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [sortBy, setSortBy] = useState<SortKey>('geo_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  // Export includes offer performance data (conversions), so gate it on the
  // offers-performance section.
  const canExport = useAuthStore((s) => s.hasSection('offers-performance'));

  const { data: adminUsers = [], isLoading: adminUsersLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => listUsers().then((result) => result.users),
    enabled: isSuperAdmin,
  });

  const handleSort = (key: string) => {
    const k = key as SortKey;
    if (k === sortBy) {
      setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(k);
      setSortOrder('asc');
    }
  };

  const {
    data: offers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['offers'],
    queryFn: getOffers,
  });
  const sourceAudit = useQuery({
    queryKey: ['funnel-audit', sourceAuditId],
    queryFn: () => getFunnelAudit(sourceAuditId),
    enabled: Boolean(sourceAuditId),
  });

  useEffect(() => {
    if (!sourceAuditId || !sourceAudit.data || prefilledAuditId.current === sourceAuditId) return;
    const audit = sourceAudit.data.audit;
    setFormData({
      ...initialFormData,
      brand: audit.offer_brand?.trim() || audit.offer_partner,
      geo: audit.geos.join(','),
      offer_url: audit.offer_url,
      mindepBl: `${(audit.min_deposit_amount / 100).toFixed(2)} ${audit.min_deposit_currency}`,
    });
    setSelectedOffer(null);
    setFormError('');
    setModalMode('create');
    prefilledAuditId.current = sourceAuditId;
  }, [sourceAuditId, sourceAudit.data]);

  const { data: allTrackerCampaigns = [] } = useQuery({
    queryKey: ['tracker-campaigns'],
    queryFn: getCampaigns,
  });
  const { data: apps = [], isSuccess: appsLoaded } = useQuery({
    queryKey: ['apps'],
    queryFn: getApps,
  });

  const bannedAppIds = useMemo(
    () => new Set(apps.filter((app) => Boolean(app.is_banned)).map((app) => app.id)),
    [apps],
  );
  const bannedBundleIds = useMemo(
    () => new Set(
      apps
        .filter((app) => Boolean(app.is_banned))
        .map((app) => app.bundle_id.trim().toLowerCase()),
    ),
    [apps],
  );
  const poolCampaigns = useMemo(
    () => appsLoaded
      ? allTrackerCampaigns.filter((campaign) =>
        campaign.destination_type === 'offer_pool'
        && (!campaign.app_id || !bannedAppIds.has(campaign.app_id))
        && !bannedBundleIds.has(campaign.name.trim().toLowerCase()))
      : [],
    [allTrackerCampaigns, appsLoaded, bannedAppIds, bannedBundleIds],
  );
  const selectablePoolCampaignIds = useMemo(
    () => new Set(poolCampaigns.map((campaign) => campaign.id)),
    [poolCampaigns],
  );
  // A brand-new offer should serve every pool campaign by default; the campaigns
  // load after the modal opens, so seed once they arrive and only once per open.
  const poolDefaultsApplied = useRef(false);
  useEffect(() => {
    if (modalMode !== 'create' || poolDefaultsApplied.current) return;
    if (poolCampaigns.length === 0) return;
    poolDefaultsApplied.current = true;
    setFormData((prev) => ({
      ...prev,
      poolCampaignIds: poolCampaigns.map((campaign) => campaign.id),
    }));
  }, [modalMode, poolCampaigns]);

  // Suggestion lists derived from existing offers (case-insensitive, sorted).
  const partnerOptions = useMemo(() => collectUniqueSorted(offers.map((o) => o.partner)), [offers]);
  const brandOptions = useMemo(() => collectUniqueSorted(offers.map((o) => o.brand)), [offers]);
  const trafficSourceOptions = useMemo(
    () => collectUniqueSorted(offers.map((o) => o.traffic_source)),
    [offers]
  );

  // Group offers by cap_group_id for shared-cap display + aggregation.
  const offersByGroup = useMemo(() => {
    const m = new Map<string, Offer[]>();
    offers.forEach((o) => {
      if (!o.cap_group_id) return;
      const list = m.get(o.cap_group_id) || [];
      list.push(o);
      m.set(o.cap_group_id, list);
    });
    return m;
  }, [offers]);

  const filteredSortedOffers = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const partnerSet = new Set(filters.partners.map((s) => s.toLowerCase()));
    const brandSet = new Set(filters.brands.map((s) => s.toLowerCase()));
    const tsSet = new Set(filters.trafficSources.map((s) => s.toLowerCase()));
    const currencySet = new Set(filters.currencies);
    const statusSet = new Set(filters.statuses);
    const geoSet = new Set(filters.geoCodes.map((s) => s.toUpperCase()));

    const passes = (o: Offer): boolean => {
      if (q) {
        const hay = [o.partner, o.brand, o.kpi, o.kpi_text, o.contact, o.offer_url, o.wager, o.mindep_bl, o.rs, o.traffic_source, o.geo, o.lang, o.created_by_name, o.created_by_email]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (partnerSet.size && !partnerSet.has((o.partner || '').toLowerCase())) return false;
      if (brandSet.size && !brandSet.has((o.brand || '').toLowerCase())) return false;
      if (tsSet.size && !tsSet.has((o.traffic_source || '').toLowerCase())) return false;
      if (filters.verticals.length && !filters.verticals.includes(o.vertical || '')) return false;
      if (currencySet.size && !currencySet.has(o.currency || 'USD')) return false;
      if (statusSet.size && !statusSet.has(o.status || 'active')) return false;
      if (geoSet.size) {
        const codes = (o.geo || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
        if (!codes.some((c) => geoSet.has(c))) return false;
      }
      if (filters.hasKpi === 'yes' && !(o.kpi_text && o.kpi_text.trim())) return false;
      if (filters.hasKpi === 'no' && o.kpi_text && o.kpi_text.trim()) return false;
      if (filters.capState !== 'all') {
        if (filters.capState === 'none' && o.cap != null) return false;
        if (filters.capState !== 'none') {
          if (o.cap == null) return false;
          const group = o.cap_group_id ? offersByGroup.get(o.cap_group_id) || [] : null;
          const isShared = !!group && group.length > 1;
          const done = isShared ? group!.reduce((s, x) => s + (x.cap_deps || 0), 0) : (o.cap_deps || 0);
          const reached = o.cap > 0 && done >= o.cap;
          if (filters.capState === 'reached' && !reached) return false;
          if (filters.capState === 'in_progress' && reached) return false;
        }
      }
      return true;
    };

    const filtered = offers.filter(passes);

    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const cmpStr = (a: string | null | undefined, b: string | null | undefined) =>
      collator.compare(a || '', b || '');
    const cmpNum = (a: number | null | undefined, b: number | null | undefined) => {
      const av = a == null ? Number.NEGATIVE_INFINITY : a;
      const bv = b == null ? Number.NEGATIVE_INFINITY : b;
      return av - bv;
    };
    const firstGeoCode = (o: Offer) =>
      (o.geo || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)[0] || '';
    const firstGeoName = (o: Offer) => {
      const c = firstGeoCode(o);
      return COUNTRY_MAP[c]?.name || c;
    };

    const sorted = [...filtered].sort((a, b) => {
      let r = 0;
      switch (sortBy) {
        case 'geo_code': r = cmpStr(firstGeoCode(a), firstGeoCode(b)); break;
        case 'geo_name': r = cmpStr(firstGeoName(a), firstGeoName(b)); break;
        case 'partner': r = cmpStr(a.partner, b.partner); break;
        case 'brand': r = cmpStr(a.brand, b.brand); break;
        case 'traffic_source': r = cmpStr(a.traffic_source, b.traffic_source); break;
        case 'vertical': r = cmpStr(a.vertical, b.vertical); break;
        case 'payout': r = cmpNum(a.payout_cents, b.payout_cents); break;
        case 'rs': r = cmpStr(a.rs, b.rs); break;
        case 'mindep_bl': r = cmpStr(a.mindep_bl, b.mindep_bl); break;
        case 'cap': r = cmpNum(a.cap, b.cap); break;
        case 'kpi_text': r = cmpStr(a.kpi_text, b.kpi_text); break;
        case 'wager': r = cmpStr(a.wager, b.wager); break;
        case 'kpi': r = cmpStr(a.kpi, b.kpi); break;
        case 'created_by': r = cmpStr(a.created_by_name || a.created_by_email, b.created_by_name || b.created_by_email); break;
        case 'created_at': r = cmpStr(a.created_at, b.created_at); break;
        case 'clicks': r = cmpNum(a.clicks, b.clicks); break;
        case 'regs': r = cmpNum(a.regs, b.regs); break;
        case 'deps': r = cmpNum(a.deps, b.deps); break;
        case 'r2d': r = offerR2d(a) - offerR2d(b); break;
        case 'status': r = cmpStr(a.status, b.status); break;
      }
      return sortOrder === 'asc' ? r : -r;
    });

    return sorted;
  }, [offers, filters, sortBy, sortOrder, offersByGroup]);

  const activeFilterCount =
    (filters.search.trim() ? 1 : 0) +
    filters.partners.length +
    filters.brands.length +
    filters.trafficSources.length +
    filters.verticals.length +
    filters.currencies.length +
    filters.statuses.length +
    filters.geoCodes.length +
    (filters.hasKpi !== 'all' ? 1 : 0) +
    (filters.capState !== 'all' ? 1 : 0);

  // Compute GEOs without active offers (paused offers don't count as coverage)
  const missingGeos = useMemo(() => {
    const coveredGeos = new Set<string>();
    offers.forEach((offer) => {
      if ((offer.status || 'active') !== 'active') return;
      offer.geo.split(',').map((s) => s.trim()).filter(Boolean).forEach((code) => {
        coveredGeos.add(code);
      });
    });
    return COUNTRIES.filter((c) => !coveredGeos.has(c.code));
  }, [offers]);

  const [copiedMissingGeos, setCopiedMissingGeos] = useState(false);
  const copyMissingGeos = async () => {
    const text = missingGeos.map((c) => c.code).join(', ');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMissingGeos(true);
      setTimeout(() => setCopiedMissingGeos(false), 1500);
    } catch {}
  };

  const createMutation = useMutation({
    mutationFn: async ({
      data,
      poolCampaignIds,
      sharedOfferIds,
      auditId,
    }: {
      data: CreateOfferInput;
      poolCampaignIds?: string[];
      sharedOfferIds?: string[];
      auditId?: string;
    }) => {
      const created = await createOffer(data);
      if (auditId) {
        await updateFunnelAudit(auditId, { offer_id: created.id });
      }
      // Pool campaigns and shared cap aren't part of the create payload —
      // wire them up post-create so the create form has parity with edit.
      if (poolCampaignIds && poolCampaignIds.length > 0) {
        await syncOfferPoolCampaigns(created.id, poolCampaignIds);
      }
      if (sharedOfferIds && sharedOfferIds.length > 0) {
        await updateOffer(created.id, { shared_offer_ids: sharedOfferIds });
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-flows'] });
      queryClient.invalidateQueries({ queryKey: ['flow-offers'] });
      queryClient.invalidateQueries({ queryKey: ['funnel-audits'] });
      if (sourceAuditId) queryClient.invalidateQueries({ queryKey: ['funnel-audit', sourceAuditId] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data, poolCampaignIds }: { id: string; data: UpdateOfferInput; poolCampaignIds?: string[] }) => {
      const updated = await updateOffer(id, data);
      if (poolCampaignIds) {
        await syncOfferPoolCampaigns(id, poolCampaignIds);
      }
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-flows'] });
      queryClient.invalidateQueries({ queryKey: ['flow-offers'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteOffer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const [distributing, setDistributing] = useState(false);
  const [distributeReport, setDistributeReport] = useState<DistributeReport | null>(null);

  const handleDistribute = async () => {
    setDistributing(true);
    setDistributeReport(null);
    const report: DistributeReport = {
      offersConsidered: 0,
      offersSkippedMultiGeo: 0,
      campaignsProcessed: 0,
      flowsCreated: 0,
      offersAttached: 0,
      offersAlreadyPresent: 0,
      errors: [],
    };

    try {
      const singleGeoOffers: Array<{ offer: Offer; geo: string }> = [];
      offers.forEach((offer) => {
        const codes = offer.geo.split(',').map((s) => s.trim()).filter(Boolean);
        if (codes.length === 1) {
          singleGeoOffers.push({ offer, geo: codes[0].toUpperCase() });
        } else if (codes.length > 1) {
          report.offersSkippedMultiGeo += 1;
        }
      });
      report.offersConsidered = singleGeoOffers.length;

      const allCampaigns = await getCampaigns();
      const campaigns = allCampaigns.filter((c) => c.destination_type === 'redirect');

      for (const campaign of campaigns) {
        try {
          const flows = await getFlows(campaign.id);
          // Build state for this campaign
          const flowsWithOffers = await Promise.all(
            flows.map(async (f) => {
              const flowOffers = await getFlowOffers(f.id);
              let countries: string[] = [];
              try {
                const parsed = JSON.parse(f.filters_json || '{}');
                if (Array.isArray(parsed.country)) {
                  countries = parsed.country.map((c: string) => String(c).toUpperCase());
                }
              } catch { /* ignore malformed */ }
              return { flow: f, offerIds: new Set(flowOffers.map((fo) => fo.offer_id)), countries };
            })
          );

          const offerIdsInCampaign = new Set<string>();
          flowsWithOffers.forEach((fw) => fw.offerIds.forEach((id) => offerIdsInCampaign.add(id)));

          // Map single-geo flows by geo → flow
          const singleGeoFlowByGeo = new Map<string, typeof flowsWithOffers[number]>();
          flowsWithOffers.forEach((fw) => {
            if (fw.countries.length === 1) {
              singleGeoFlowByGeo.set(fw.countries[0], fw);
            }
          });

          for (const { offer, geo } of singleGeoOffers) {
            if (offerIdsInCampaign.has(offer.id)) {
              report.offersAlreadyPresent += 1;
              continue;
            }
            const existing = singleGeoFlowByGeo.get(geo);
            if (existing) {
              await attachOffer(existing.flow.id, offer.id, 100);
              existing.offerIds.add(offer.id);
              offerIdsInCampaign.add(offer.id);
              report.offersAttached += 1;
            } else {
              const newFlow = await createFlow({
                campaign_id: campaign.id,
                name: `AUTO ${geo}`,
                priority: 100,
                status: 'active',
                filters_json: JSON.stringify({ country: [geo] }),
              });
              await attachOffer(newFlow.id, offer.id, 100);
              const wrapped = { flow: newFlow, offerIds: new Set<string>([offer.id]), countries: [geo] };
              singleGeoFlowByGeo.set(geo, wrapped);
              offerIdsInCampaign.add(offer.id);
              report.flowsCreated += 1;
              report.offersAttached += 1;
            }
          }
          report.campaignsProcessed += 1;
        } catch (err) {
          report.errors.push(`Campaign "${campaign.name}": ${err instanceof Error ? err.message : String(err)}`);
        }
      }

      queryClient.invalidateQueries({ queryKey: ['tracker-flows'] });
      queryClient.invalidateQueries({ queryKey: ['flow-offers'] });
    } catch (err) {
      report.errors.push(err instanceof Error ? err.message : String(err));
    } finally {
      setDistributing(false);
      setDistributeReport(report);
    }
  };

  const openCreateModal = () => {
    setFormData(initialFormData);
    setFormError('');
    setSelectedOffer(null);
    setDuplicateSource(null);
    poolDefaultsApplied.current = false;
    setModalMode('create');
  };

  // Keep the edited offer's fields, drop what has to stay unique to the original:
  // the shared cap group links the copy back to the source's cap.
  const duplicateOffer = () => {
    if (!selectedOffer) return;
    setFormData((prev) => ({
      ...prev,
      sharedCap: false,
      sharedOfferIds: [],
      resetCapDeps: false,
    }));
    setDuplicateSource(selectedOffer);
    setSelectedOffer(null);
    setFormError('');
    // A copy keeps the source's campaigns — don't let the create default overwrite them.
    poolDefaultsApplied.current = true;
    setModalMode('create');
  };

  const openEditModal = async (offer: Offer) => {
    let poolIds: string[] = [];
    try {
      poolIds = await getOfferPoolCampaigns(offer.id);
    } catch {
      // ignore — multi-select will start empty
    }
    setFormData({
      partner: offer.partner,
      geo: offer.geo,
      brand: offer.brand || '',
      payout: offer.payout_cents != null ? (offer.payout_cents / 100).toFixed(2) : '',
      currency: (offer.currency as 'USD' | 'EUR') || 'USD',
      lang: offer.lang || '',
      description: offer.kpi || '',
      contact: offer.contact || '',
      offer_url: offer.offer_url || '',
      cap: offer.cap != null ? String(offer.cap) : '',
      wager: offer.wager || '',
      kpiText: offer.kpi_text || '',
      mindepBl: offer.mindep_bl || '',
      rs: offer.rs || '',
      trafficSource: offer.traffic_source || '',
      vertical: offer.vertical || '',
      dealModel: offer.deal_model || 'cpa',
      budget: offer.budget || '',
      payType: offer.pay_type || '',
      reportUrl: offer.report_url || '',
      sharedCap: !!offer.cap_group_id,
      sharedOfferIds: offer.cap_group_id
        ? (offersByGroup.get(offer.cap_group_id) || [])
            .filter((o) => o.id !== offer.id)
            .map((o) => o.id)
        : [],
      resetCapDeps: false,
      status: (offer.status as 'active' | 'paused') || 'active',
      poolCampaignIds: poolIds,
      responsibleAdminId: offer.created_by_admin_id || '',
    });
    setFormError('');
    setSelectedOffer(offer);
    setModalMode('edit');
  };

  const openDeleteModal = (offer: Offer) => {
    setSelectedOffer(offer);
    setFormError('');
    setModalMode('delete');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedOffer(null);
    setDuplicateSource(null);
    setFormData(initialFormData);
    setFormError('');
    if (sourceAuditId) {
      const next = new URLSearchParams(searchParams);
      next.delete('from_audit');
      setSearchParams(next, { replace: true });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.partner.trim()) {
      setFormError('Partner is required');
      return;
    }
    if (!formData.geo.trim()) {
      setFormError('GEO is required');
      return;
    }
    if (sourceAudit.data) {
      const audit = sourceAudit.data.audit;
      const brandName = audit.offer_brand?.trim() || audit.offer_partner;
      const geos = formData.geo.split(',').map((value) => value.trim().toUpperCase());
      if (formData.brand.trim().toLowerCase() !== brandName.toLowerCase()) {
        setFormError(`Keep brand “${brandName}” to connect this offer to the audit`);
        return;
      }
      const missingGeos = audit.geos.filter((geo) => !geos.includes(geo));
      if (missingGeos.length > 0) {
        setFormError(`Keep audit GEOs ${missingGeos.join(', ')} to connect this offer`);
        return;
      }
    }

    const capRaw = formData.cap.trim();
    const capParsed = capRaw === '' ? null : Math.max(0, Math.floor(Number(capRaw)));
    if (capRaw !== '' && (!Number.isFinite(capParsed) || capParsed === null)) {
      setFormError('Cap must be a positive number');
      return;
    }

    const payoutRaw = formData.payout.trim();
    let payoutCentsParsed: number | null = null;
    if (payoutRaw !== '') {
      const dollars = Number(payoutRaw);
      if (!Number.isFinite(dollars) || dollars < 0) {
        setFormError('Payout must be a positive number');
        return;
      }
      payoutCentsParsed = Math.round(dollars * 100);
    }

    const basePayload = {
      partner: formData.partner.trim(),
      geo: formData.geo.trim(),
      brand: formData.brand.trim() || null,
      payout_cents: payoutCentsParsed,
      currency: formData.currency,
      lang: formData.lang.trim() || null,
      kpi: formData.description.trim() || null,
      contact: formData.contact.trim() || null,
      offer_url: formData.offer_url.trim() || null,
      cap: capParsed,
      wager: formData.wager.trim() || null,
      kpi_text: formData.kpiText.trim() || null,
      mindep_bl: formData.mindepBl.trim() || null,
      rs: formData.rs.trim() || null,
      traffic_source: formData.trafficSource.trim() || null,
      vertical: formData.vertical || null,
      deal_model: formData.dealModel,
      budget: formData.budget.trim() || null,
      pay_type: formData.payType || null,
      report_url: formData.reportUrl.trim() || null,
    };

    if (modalMode === 'create') {
      createMutation.mutate({
        data: { ...basePayload, status: formData.status },
        poolCampaignIds: formData.poolCampaignIds,
        sharedOfferIds: formData.sharedCap ? formData.sharedOfferIds : [],
        auditId: sourceAuditId || undefined,
      });
    } else if (modalMode === 'edit' && selectedOffer) {
      updateMutation.mutate({
        id: selectedOffer.id,
        data: {
          ...basePayload,
          status: formData.status,
          reset_cap_deps: formData.resetCapDeps || undefined,
          shared_offer_ids: formData.sharedCap ? formData.sharedOfferIds : [],
          ...(isSuperAdmin && formData.responsibleAdminId !== (selectedOffer.created_by_admin_id || '')
            ? { created_by_admin_id: formData.responsibleAdminId || null }
            : {}),
        },
        poolCampaignIds: formData.poolCampaignIds,
      });
    }
  };

  const handleDelete = () => {
    if (selectedOffer) {
      deleteMutation.mutate(selectedOffer.id);
    }
  };

  const cellBase = 'px-4 py-3.5 text-sm text-content align-top';
  const headerBase = 'px-4';

  const geoCodes = (offer: Offer) =>
    offer.geo.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
  const geoCodesText = (offer: Offer) => geoCodes(offer).join(', ') || '—';
  const geoNamesText = (offer: Offer) => {
    const codes = geoCodes(offer);
    if (codes.length === 0) return '—';
    const names = codes.map((c) => COUNTRY_MAP[c]?.name || c);
    if (names.length <= 3) return names.join(', ');
    return names.slice(0, 3).join(', ') + ` +${names.length - 3}`;
  };

  const plainCell = (text: string | null | undefined, extra = '') => (
    <span className={`text-content-muted text-sm block truncate ${extra}`} title={text || ''}>
      {text || '—'}
    </span>
  );

  const columns = [
    {
      key: 'geo',
      label: 'GEO',
      sortKey: 'geo_name',
      headerClassName: `${headerBase} w-[130px]`,
      cellClassName: `${cellBase} w-[130px]`,
      render: (offer: Offer) => {
        const codes = geoCodes(offer);
        return (
          <div title={`${geoCodesText(offer)} · ${geoNamesText(offer)}`}>
            <div className="flex items-center gap-2 font-semibold text-content">
              <span className="text-base leading-none" aria-hidden>
                {codes.length === 1 ? (COUNTRY_MAP[codes[0]]?.flag || '🌍') : '🌍'}
              </span>
              <span className="truncate uppercase">{geoCodesText(offer)}</span>
            </div>
            <span className="mt-1 block truncate text-xs text-content-subtle">
              {geoNamesText(offer)}
            </span>
          </div>
        );
      },
    },
    {
      key: 'offer',
      label: 'Offer',
      sortKey: 'partner',
      headerClassName: `${headerBase} w-[190px]`,
      cellClassName: `${cellBase} w-[190px]`,
      render: (offer: Offer) => (
        <div title={[offer.partner, offer.brand].filter(Boolean).join(' · ')}>
          <div className="flex min-w-0 items-center gap-2">
            <span className="block min-w-0 truncate font-semibold text-content">{offer.partner}</span>
            <span
              className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                offer.status === 'paused'
                  ? 'bg-warning-soft text-warning'
                  : 'bg-success-soft text-success'
              }`}
            >
              {offer.status === 'paused' ? 'Paused' : 'Active'}
            </span>
          </div>
          <span className="mt-1 block truncate text-xs text-content-muted">
            {offer.brand || 'No brand'}
          </span>
        </div>
      ),
    },
    {
      key: 'traffic',
      label: 'Traffic',
      sortKey: 'traffic_source',
      headerClassName: `${headerBase} w-[145px]`,
      cellClassName: `${cellBase} w-[145px]`,
      render: (offer: Offer) => (
        <div>
          {plainCell(offer.traffic_source, 'font-medium text-content')}
          <div className="mt-1.5 flex items-center gap-2">
            {offer.vertical && (
              <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${VERTICAL_BADGE[offer.vertical]}`}>
                {verticalLabel(offer.vertical)}
              </span>
            )}
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                offer.deal_model === 'spend'
                  ? 'bg-primary/10 text-primary'
                  : 'bg-surface-muted text-content-muted'
              }`}
            >
              {offer.deal_model === 'spend' ? 'Spend' : 'CPA'}
            </span>
            {offer.report_url && (
              <a
                href={offer.report_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                title="Open spend report"
              >
                Report
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7m0 0v7m0-7L10 14M5 5h5M5 5v14h14v-5" />
                </svg>
              </a>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'terms',
      label: 'Terms',
      sortKey: 'payout',
      headerClassName: `${headerBase} w-[210px]`,
      cellClassName: `${cellBase} w-[210px]`,
      render: (offer: Offer) => (
        <div>
          <span className="font-semibold tabular-nums text-content">
            {fmtPayout(offer.payout_cents, offer.currency)}
          </span>
          <span className="ml-1.5 text-[10px] uppercase text-content-subtle">payout</span>
          <div className="mt-1.5 space-y-1 text-xs">
            <div className="flex min-w-0 items-center gap-2">
              <span className="w-[48px] shrink-0 text-content-subtle">Min dep</span>
              <span className="truncate text-content-muted" title={offer.mindep_bl || ''}>
                {offer.mindep_bl || '—'}
              </span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="w-[48px] shrink-0 text-content-subtle">RS</span>
              <span className="truncate text-content-muted" title={offer.rs || ''}>
                {offer.rs || '—'}
              </span>
            </div>
          </div>
          {(offer.budget || offer.pay_type) && (
            <div className="mt-1 truncate text-xs text-content-subtle" title={[offer.budget, offer.pay_type].filter(Boolean).join(' · ')}>
              {[offer.budget, offer.pay_type].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'limits',
      label: 'Limits & KPI',
      sortKey: 'cap',
      headerClassName: `${headerBase} w-[220px]`,
      cellClassName: `${cellBase} w-[220px]`,
      render: (offer: Offer) => {
        const group = offer.cap_group_id ? offersByGroup.get(offer.cap_group_id) || [] : null;
        const isShared = !!group && group.length > 1;
        const done = isShared
          ? group.reduce((sum, o) => sum + (o.cap_deps || 0), 0)
          : offer.cap_deps;
        const total = offer.cap || 0;
        const pctVal = total > 0 ? Math.min(100, (done / total) * 100) : 0;
        const reached = total > 0 && done >= total;
        return (
          <div>
            {offer.cap == null ? (
              <div className="text-xs text-content-subtle">No cap</div>
            ) : (
              <>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className={`font-medium tabular-nums ${reached ? 'text-success' : 'text-content-muted'}`}>
                    {done}/{total}
                  </span>
                  <span className="flex items-center gap-1 text-content-subtle">
                    {isShared && (
                      <span
                        title={`Shared with ${group!.length - 1} other offer(s)`}
                        className="inline-flex items-center rounded bg-primary/10 px-1 py-0.5 text-[10px] font-medium text-primary"
                      >
                        shared ×{group!.length}
                      </span>
                    )}
                    {pctVal.toFixed(0)}%
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted">
                  <div
                    className={`h-full ${reached ? 'bg-success' : 'bg-primary'}`}
                    style={{ width: `${pctVal}%` }}
                  />
                </div>
              </>
            )}
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex min-w-0 items-center gap-2">
                <span className="w-[42px] shrink-0 text-content-subtle">KPI</span>
                <span className="truncate text-content-muted" title={offer.kpi_text || ''}>
                  {offer.kpi_text || '—'}
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <span className="w-[42px] shrink-0 text-content-subtle">Wager</span>
                <span className="truncate text-content-muted" title={offer.wager || ''}>
                  {offer.wager || '—'}
                </span>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'performance',
      label: 'Performance',
      sortKey: 'r2d',
      headerClassName: `${headerBase} w-[190px]`,
      cellClassName: `${cellBase} w-[190px]`,
      render: (offer: Offer) => (
        <div className="grid grid-cols-4 gap-2 text-center tabular-nums">
          {[
            ['Clicks', (offer.clicks ?? 0).toLocaleString()],
            ['Regs', (offer.regs ?? 0).toLocaleString()],
            ['Deps', (offer.deps ?? 0).toLocaleString()],
            ['R2D', `${offerR2d(offer).toFixed(1)}%`],
          ].map(([label, value]) => (
            <div key={label} className="min-w-0">
              <span className="block truncate text-[10px] uppercase tracking-wide text-content-subtle">{label}</span>
              <span className={`mt-0.5 block truncate text-xs font-medium ${label === 'R2D' ? 'text-primary' : 'text-content-muted'}`}>
                {value}
              </span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortKey: 'created_at',
      headerClassName: `${headerBase} w-[210px]`,
      cellClassName: `${cellBase} w-[210px] whitespace-nowrap`,
      render: (offer: Offer) => {
        const responsible = offer.created_by_name || offer.created_by_email;
        return (
          <div title={`Created ${formatDateTime(offer.created_at)}`}>
            <span className="block text-xs text-content-muted">{formatDateTime(offer.created_at)}</span>
            <span className="mt-1 block text-[11px] text-content-subtle">Running for {offerAge(offer.created_at)}</span>
            <div
              className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs text-content-subtle"
              title={[responsible, offer.created_by_email].filter(Boolean).join(' · ')}
            >
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 21a8 8 0 00-16 0m8-10a4 4 0 100-8 4 4 0 000 8z" />
              </svg>
              <span className="truncate">{responsible || 'Unassigned'}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      headerClassName: `${headerBase} w-[44px]`,
      cellClassName: `${cellBase} w-[44px] whitespace-nowrap`,
      render: (offer: Offer) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            openDeleteModal(offer);
          }}
          className="text-content-subtle hover:text-danger transition-colors"
          title="Delete offer"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      ),
    },
  ];

  if (error) {
    return (
      <div className="bg-danger-soft border border-danger/30 rounded-lg p-4">
        <p className="text-sm text-danger">
          Failed to load offers: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Offers</h1>
          <p className="text-sm text-content-subtle mt-1">
            Manage partner offers and GEO coverage
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {canExport && (
            <Button
              variant="secondary"
              onClick={() => setExportModalOpen(true)}
              disabled={filteredSortedOffers.length === 0}
              title="Export offers"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
              </svg>
              Export
            </Button>
          )}
          <EditOnly>
            <Button
              variant="secondary"
              onClick={() => {
                setDistributeReport(null);
                setModalMode('distribute');
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7M17 16l4 4m0-4l-4 4" />
              </svg>
              Distribute to campaigns
            </Button>
          </EditOnly>
          <EditOnly>
            <Button variant="secondary" onClick={() => setModalMode('pool-coverage')} title="Active offers not yet in every pool campaign">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Missing campaigns
            </Button>
          </EditOnly>
          <Button variant="secondary" onClick={() => setModalMode('missing-geo')}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            GEO without offers ({missingGeos.length})
          </Button>
          <Button variant="secondary" onClick={() => setModalMode('geo-coverage')}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 19V9m6 10V5m6 14v-7m4 7H2" />
            </svg>
            Офферы по GEO
          </Button>
          <Button variant="secondary" onClick={() => setModalMode('traffic-geo-gaps')}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Трафик без офера
          </Button>
          <EditOnly>
            <Button onClick={openCreateModal}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Offer
            </Button>
          </EditOnly>
        </div>
      </div>

      {sourceAudit.isError && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          Could not load the funnel audit for offer prefilling.
        </div>
      )}

      {/* Filters */}
      <OfferFilters
        filters={filters}
        onChange={setFilters}
        partnerOptions={partnerOptions}
        brandOptions={brandOptions}
        trafficSourceOptions={trafficSourceOptions}
        activeCount={activeFilterCount}
        totalCount={offers.length}
        filteredCount={filteredSortedOffers.length}
        onClear={() => setFilters(initialFilters)}
      />

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredSortedOffers}
        loading={isLoading}
        onRowClick={openEditModal}
        keyExtractor={(offer) => offer.id}
        rowClassName={(offer) =>
          offer.status === 'paused'
            ? ''
            : '!bg-success-soft hover:!bg-success-soft'
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        fixedLayout
        minWidth="1300px"
      />

      <ExportModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        offers={filteredSortedOffers}
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? (duplicateSource ? 'Duplicate Offer' : 'Add Offer') : 'Edit Offer'}
        size="5xl"
      >
        <form onSubmit={handleSubmit}>
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md mb-4">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}
          {sourceAudit.data && modalMode === 'create' && (
            <div className="mb-4 rounded-md border border-primary/25 bg-primary/5 p-3 text-sm text-content-muted">
              Creating an offer for <span className="font-medium text-content">{sourceAudit.data.audit.offer_brand || sourceAudit.data.audit.offer_partner}</span> from its funnel audit. Brand, GEO, URL and minimum deposit are prefilled; saving will connect the new offer to the audit.
            </div>
          )}
          {duplicateSource && modalMode === 'create' && (
            <div className="mb-4 rounded-md border border-primary/25 bg-primary/5 p-3 text-sm text-content-muted">
              Копия оффера{' '}
              <span className="font-medium text-content">
                {duplicateSource.brand || duplicateSource.partner}
              </span>
              {duplicateSource.geo ? ` (${duplicateSource.geo})` : ''}. Поменяйте бренд и остальные отличия — исходный оффер не изменится.
            </div>
          )}
          {modalMode === 'edit' && selectedOffer && (
            <div className="mb-4 rounded-md border border-border bg-surface-muted px-3 py-2 text-xs text-content-muted">
              Responsible:{' '}
              <span className="font-medium text-content">
                {selectedOffer.created_by_name || selectedOffer.created_by_email || 'Unknown'}
              </span>
              {selectedOffer.created_by_email && selectedOffer.created_by_email !== selectedOffer.created_by_name && (
                <span className="text-content-subtle"> ({selectedOffer.created_by_email})</span>
              )}
              <span className="text-content-subtle"> · created {formatDateTime(selectedOffer.created_at)}</span>
            </div>
          )}

          {(() => {
            const inputCls = "block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm";
            const labelCls = "block text-sm font-medium text-content-muted mb-1";
            const sectionTitle = "text-xs font-semibold uppercase tracking-wide text-content-subtle mb-3";
            return (
            <div className="space-y-6">
              {/* Status toggle */}
              <div className="flex items-center justify-between gap-3 p-3 bg-surface-muted rounded-md border border-border">
                  <div>
                    <div className="text-sm font-medium text-content">Status</div>
                    <div className="text-xs text-content-subtle">Paused offers are hidden from traffic routing.</div>
                  </div>
                  <div className="inline-flex rounded-md border border-border overflow-hidden bg-surface">
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, status: 'active' }))}
                      className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${
                        formData.status === 'active'
                          ? 'bg-success-soft text-success font-medium'
                          : 'text-content-muted hover:bg-surface-muted'
                      }`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${formData.status === 'active' ? 'bg-success' : 'bg-border-strong'}`} />
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, status: 'paused' }))}
                      className={`px-3 py-1.5 text-sm flex items-center gap-1.5 border-l border-border ${
                        formData.status === 'paused'
                          ? 'bg-warning-soft text-warning font-medium'
                          : 'text-content-muted hover:bg-surface-muted'
                      }`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${formData.status === 'paused' ? 'bg-amber-500' : 'bg-border-strong'}`} />
                      Paused
                    </button>
                  </div>
                </div>

              {modalMode === 'edit' && isSuperAdmin && (
                <div className="rounded-md border border-border bg-surface-muted p-3">
                  <label className="mb-1 block text-sm font-medium text-content">Responsible</label>
                  <Select
                    value={formData.responsibleAdminId}
                    onChange={(value) => setFormData((prev) => ({ ...prev, responsibleAdminId: value }))}
                    options={[
                      { value: '', label: 'Unassigned' },
                      ...adminUsers
                        .filter((user) => user.is_active || user.id === selectedOffer?.created_by_admin_id)
                        .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))
                        .map((user) => ({
                          value: user.id,
                          label: `${user.name || user.email}${user.is_active ? '' : ' (inactive)'}`,
                        })),
                    ]}
                    searchable
                    disabled={adminUsersLoading}
                    placeholder={adminUsersLoading ? 'Loading users…' : 'Select responsible'}
                  />
                  <p className="mt-1 text-xs text-content-subtle">
                    Only super admins can reassign an offer.
                  </p>
                </div>
              )}

              {/* Identity */}
              <section>
                <h3 className={sectionTitle}>Identity</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="partner" className={labelCls}>Partner *</label>
                    <Combobox
                      id="partner"
                      value={formData.partner}
                      options={partnerOptions}
                      onChange={(v) => setFormData((prev) => ({ ...prev, partner: v }))}
                      placeholder="Affiliate network name"
                    />
                  </div>
                  <div>
                    <label htmlFor="brand" className={labelCls}>Brand</label>
                    <Combobox
                      id="brand"
                      value={formData.brand}
                      options={brandOptions}
                      onChange={(v) => setFormData((prev) => ({ ...prev, brand: v }))}
                      placeholder="e.g. 1xBet"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="geo" className={labelCls}>GEO *</label>
                    <CountryMultiSelect
                      value={formData.geo
                        .split(',')
                        .map((s) => s.trim().toUpperCase())
                        .filter(Boolean)}
                      onChange={(codes) =>
                        setFormData((prev) => ({ ...prev, geo: codes.join(',') }))
                      }
                      emptyHint="Select one or more countries this offer covers."
                    />
                  </div>
                </div>
              </section>

              {/* Commercials */}
              <section>
                <h3 className={sectionTitle}>Commercials</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="payout" className={labelCls}>Payout</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        id="payout"
                        min="0"
                        step="0.01"
                        value={formData.payout}
                        onChange={(e) => setFormData((prev) => ({ ...prev, payout: e.target.value }))}
                        className={`${inputCls} flex-1 min-w-0`}
                        placeholder="35.00"
                      />
                      <div className="w-28 shrink-0">
                        <Select
                          id="currency"
                          value={formData.currency}
                          onChange={(v) => setFormData((prev) => ({ ...prev, currency: v as 'USD' | 'EUR' }))}
                          options={[
                            { value: 'USD', label: 'USD' },
                            { value: 'EUR', label: 'EUR' },
                          ]}
                          size="sm"
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="rs" className={labelCls}>RS</label>
                    <input
                      type="text"
                      id="rs"
                      value={formData.rs}
                      onChange={(e) => setFormData((prev) => ({ ...prev, rs: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. 40%"
                    />
                  </div>
                  <div>
                    <label htmlFor="traffic_source" className={labelCls}>Source</label>
                    <input
                      type="text"
                      id="traffic_source"
                      value={formData.trafficSource}
                      onChange={(e) => setFormData((prev) => ({ ...prev, trafficSource: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. Facebook, UAC, PPC"
                    />
                  </div>
                  <div>
                    <label htmlFor="vertical" className={labelCls}>Vertical</label>
                    <Select
                      id="vertical"
                      value={formData.vertical}
                      onChange={(v) => setFormData((prev) => ({ ...prev, vertical: v as '' | Vertical }))}
                      options={[{ value: '', label: '—' }, ...VERTICAL_OPTIONS]}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="lang" className={labelCls}>Language</label>
                    <input
                      type="text"
                      id="lang"
                      value={formData.lang}
                      onChange={(e) => setFormData((prev) => ({ ...prev, lang: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. en or en,es"
                    />
                  </div>
                  <div>
                    <label htmlFor="deal_model" className={labelCls}>Deal model</label>
                    <Select
                      id="deal_model"
                      value={formData.dealModel}
                      onChange={(v) => setFormData((prev) => ({ ...prev, dealModel: v as 'cpa' | 'spend' }))}
                      options={[
                        { value: 'cpa', label: 'CPA' },
                        { value: 'spend', label: 'Spend' },
                      ]}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="pay_type" className={labelCls}>Payment</label>
                    <Select
                      id="pay_type"
                      value={formData.payType}
                      onChange={(v) => setFormData((prev) => ({ ...prev, payType: v as '' | 'prepay' | 'postpay' }))}
                      options={[
                        { value: '', label: '—' },
                        { value: 'prepay', label: 'Prepay' },
                        { value: 'postpay', label: 'Postpay' },
                      ]}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="budget" className={labelCls}>Advertiser budget</label>
                    <input
                      type="text"
                      id="budget"
                      value={formData.budget}
                      onChange={(e) => setFormData((prev) => ({ ...prev, budget: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. $5k/day"
                    />
                  </div>
                  <div>
                    <label htmlFor="report_url" className={labelCls}>Report link</label>
                    <input
                      type="url"
                      id="report_url"
                      value={formData.reportUrl}
                      onChange={(e) => setFormData((prev) => ({ ...prev, reportUrl: e.target.value }))}
                      className={inputCls}
                      placeholder="https://docs.google.com/spreadsheets/..."
                    />
                  </div>
                </div>
              </section>

              {/* KPI & restrictions */}
              <section>
                <h3 className={sectionTitle}>KPI & restrictions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="kpi_text" className={labelCls}>KPI</label>
                    <input
                      type="text"
                      id="kpi_text"
                      value={formData.kpiText}
                      onChange={(e) => setFormData((prev) => ({ ...prev, kpiText: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. FTD $20, qFTD"
                    />
                  </div>
                  <div>
                    <label htmlFor="wager" className={labelCls}>Wager</label>
                    <input
                      type="text"
                      id="wager"
                      value={formData.wager}
                      onChange={(e) => setFormData((prev) => ({ ...prev, wager: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. x35"
                    />
                  </div>
                  <div>
                    <label htmlFor="mindep_bl" className={labelCls}>Mindep / BL</label>
                    <input
                      type="text"
                      id="mindep_bl"
                      value={formData.mindepBl}
                      onChange={(e) => setFormData((prev) => ({ ...prev, mindepBl: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. min $20"
                    />
                  </div>
                  <div>
                    <label htmlFor="cap" className={labelCls}>Cap (deposits)</label>
                    <input
                      type="number"
                      id="cap"
                      min="0"
                      step="1"
                      value={formData.cap}
                      onChange={(e) => setFormData((prev) => ({ ...prev, cap: e.target.value }))}
                      className={inputCls}
                      placeholder="empty = no cap"
                    />
                  </div>
                </div>
                <p className="mt-2 text-xs text-content-subtle">
                  Cap is the number of deposits required to hit KPI. Counter is incremented automatically by deposit postbacks.
                </p>

                {modalMode === 'edit' && selectedOffer && (() => {
                  const groupMembers = selectedOffer.cap_group_id
                    ? offersByGroup.get(selectedOffer.cap_group_id) || []
                    : [];
                  const isShared = groupMembers.length > 1;
                  const displayDone = isShared
                    ? groupMembers.reduce((s, o) => s + (o.cap_deps || 0), 0)
                    : selectedOffer.cap_deps;
                  return (
                    <div className="mt-3 flex items-center justify-between gap-3 p-2 bg-surface-muted rounded-md">
                      <span className="text-xs text-content-muted">
                        {isShared ? 'Pooled progress' : 'Current progress'}:{' '}
                        <span className="font-medium text-content">
                          {displayDone}
                          {selectedOffer.cap != null ? ` / ${selectedOffer.cap}` : ''} deposits
                        </span>
                      </span>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={formData.resetCapDeps}
                          onChange={(e) => setFormData((prev) => ({ ...prev, resetCapDeps: e.target.checked }))}
                          className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                        />
                        <span className="text-xs text-content-muted">Reset counter to 0</span>
                      </label>
                    </div>
                  );
                })()}

                {poolCampaigns.length > 0 && (
                  <div className="mt-3 rounded-md border border-border p-3 bg-surface-muted">
                    <div className="flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-content">Offer-pool campaigns</label>
                      {(() => {
                        const selectedCount = formData.poolCampaignIds.filter((id) =>
                          selectablePoolCampaignIds.has(id)
                        ).length;
                        const allSelected = selectedCount === poolCampaigns.length;
                        return (
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({
                              ...prev,
                              poolCampaignIds: [
                                // Links to hidden banned-app campaigns stay untouched.
                                ...prev.poolCampaignIds.filter((id) => !selectablePoolCampaignIds.has(id)),
                                ...(allSelected ? [] : poolCampaigns.map((c) => c.id)),
                              ],
                            }))}
                            className="shrink-0 text-xs font-medium text-primary hover:underline"
                          >
                            {allSelected
                              ? `Снять все (${poolCampaigns.length})`
                              : `Выбрать все (${poolCampaigns.length})`}
                          </button>
                        );
                      })()}
                    </div>
                    <p className="mt-1 text-xs text-content-subtle">
                      Pick the offer-pool campaigns this offer should serve. Flows are
                      created/reused per GEO automatically; if several offers share a GEO
                      in the same campaign they auto-split evenly.
                    </p>
                    <div className="mt-3">
                      <MultiSelect
                        value={formData.poolCampaignIds.filter((id) => selectablePoolCampaignIds.has(id))}
                        onChange={(ids) => setFormData((prev) => ({
                          ...prev,
                          // Keep pre-existing links to now-hidden banned-app campaigns.
                          // Editing unrelated offer fields must not silently detach them.
                          poolCampaignIds: [
                            ...prev.poolCampaignIds.filter((id) => !selectablePoolCampaignIds.has(id)),
                            ...ids,
                          ],
                        }))}
                        options={poolCampaigns.map((c) => ({ value: c.id, label: c.name }))}
                        searchable
                        placeholder="Pick campaigns…"
                      />
                    </div>
                  </div>
                )}

                <div className="mt-3 rounded-md border border-border p-3 bg-surface-muted">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formData.sharedCap}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          sharedCap: e.target.checked,
                          sharedOfferIds: e.target.checked ? prev.sharedOfferIds : [],
                        }))
                      }
                      className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-content">Shared cap</span>
                  </label>
                  <p className="mt-1 text-xs text-content-subtle">
                    Pool the cap counter across multiple offers. Cap-reached checks and
                    reports aggregate <code>cap_deps</code> across the whole group.
                  </p>
                  {formData.sharedCap && (
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-content-muted mb-1">
                        Share with offers
                      </label>
                      <MultiSelect
                        value={formData.sharedOfferIds}
                        onChange={(ids) => setFormData((prev) => ({ ...prev, sharedOfferIds: ids }))}
                        options={offers
                          .filter((o) => !selectedOffer || o.id !== selectedOffer.id)
                          .map((o) => ({
                            value: o.id,
                            label: `${o.partner}${o.brand ? ' · ' + o.brand : ''} — ${o.geo}`,
                          }))}
                        searchable
                        placeholder="Pick offers to pool with…"
                      />
                    </div>
                  )}
                </div>
              </section>

              {/* Link & description */}
              <section>
                <h3 className={sectionTitle}>Link & description</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="offer_url" className={labelCls}>Offer URL</label>
                    <input
                      type="url"
                      id="offer_url"
                      value={formData.offer_url}
                      onChange={(e) => setFormData((prev) => ({ ...prev, offer_url: e.target.value }))}
                      className={inputCls}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label htmlFor="description" className={labelCls}>Description</label>
                    <textarea
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                      className={`${inputCls} resize-y`}
                      placeholder="Offer description, KPI, conditions, etc."
                    />
                  </div>
                </div>
              </section>
            </div>
            );
          })()}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-5 mt-6 border-t border-border">
            {modalMode === 'edit' && selectedOffer && (
              <EditOnly>
                <div className="mr-auto flex flex-wrap items-center gap-3">
                  <Button type="button" variant="secondary" onClick={duplicateOffer}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h9a2 2 0 012 2v9a2 2 0 01-2 2h-2M5 8h9a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9a2 2 0 012-2z" />
                    </svg>
                    Дублировать
                  </Button>
                  <Button type="button" variant="danger" onClick={() => openDeleteModal(selectedOffer)}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Удалить
                  </Button>
                </div>
              </EditOnly>
            )}
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {modalMode === 'create' ? (duplicateSource ? 'Create Copy' : 'Add Offer') : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modalMode === 'delete'}
        onClose={closeModal}
        title="Delete Offer"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}
          <p className="text-sm text-content-muted">
            Are you sure you want to delete offer from <strong>{selectedOffer?.partner}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Distribute Modal */}
      <Modal
        isOpen={modalMode === 'distribute'}
        onClose={() => {
          if (!distributing) closeModal();
        }}
        title="Distribute offers to campaigns"
        size="lg"
      >
        <div className="space-y-4">
          {!distributeReport && !distributing && (
            <>
              <p className="text-sm text-content-muted">
                For each offer with a single GEO, checks all <strong>redirect</strong> campaigns:
              </p>
              <ul className="text-sm text-content-muted list-disc pl-5 space-y-1">
                <li>If the offer is already in any flow of the campaign — skip.</li>
                <li>If a flow with exactly that single GEO already exists — attach the offer to it (priority 100).</li>
                <li>Otherwise — create a new flow named <code className="px-1 bg-surface-muted rounded">AUTO &lt;GEO&gt;</code> with that GEO and attach the offer.</li>
                <li>Offers with multiple GEOs are skipped entirely.</li>
                <li>Flows with more than one country are not touched.</li>
              </ul>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
                <Button type="button" onClick={handleDistribute}>Run</Button>
              </div>
            </>
          )}

          {distributing && (
            <div className="py-8 text-center">
              <div className="inline-block animate-spin h-8 w-8 border-4 border-border border-t-primary rounded-full mb-3" />
              <p className="text-sm text-content-muted">Distributing offers… do not close this window.</p>
            </div>
          )}

          {distributeReport && !distributing && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-surface-muted rounded-md">
                  <div className="text-xs text-content-subtle">Single-GEO offers processed</div>
                  <div className="text-lg font-semibold text-content">{distributeReport.offersConsidered}</div>
                </div>
                <div className="p-3 bg-surface-muted rounded-md">
                  <div className="text-xs text-content-subtle">Multi-GEO offers skipped</div>
                  <div className="text-lg font-semibold text-content">{distributeReport.offersSkippedMultiGeo}</div>
                </div>
                <div className="p-3 bg-surface-muted rounded-md">
                  <div className="text-xs text-content-subtle">Redirect campaigns processed</div>
                  <div className="text-lg font-semibold text-content">{distributeReport.campaignsProcessed}</div>
                </div>
                <div className="p-3 bg-surface-muted rounded-md">
                  <div className="text-xs text-content-subtle">Already present (skipped)</div>
                  <div className="text-lg font-semibold text-content">{distributeReport.offersAlreadyPresent}</div>
                </div>
                <div className="p-3 bg-success-soft rounded-md">
                  <div className="text-xs text-success">Flows created</div>
                  <div className="text-lg font-semibold text-success">{distributeReport.flowsCreated}</div>
                </div>
                <div className="p-3 bg-success-soft rounded-md">
                  <div className="text-xs text-success">Offers attached</div>
                  <div className="text-lg font-semibold text-success">{distributeReport.offersAttached}</div>
                </div>
              </div>
              {distributeReport.errors.length > 0 && (
                <div className="p-3 bg-danger-soft border border-danger/30 rounded-md max-h-48 overflow-y-auto">
                  <div className="text-xs font-medium text-danger mb-1">Errors ({distributeReport.errors.length})</div>
                  <ul className="text-xs text-danger space-y-1 list-disc pl-5">
                    {distributeReport.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="secondary" onClick={closeModal}>Close</Button>
                <Button type="button" onClick={handleDistribute}>Run again</Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Missing GEO Modal */}
      <Modal
        isOpen={modalMode === 'missing-geo'}
        onClose={closeModal}
        title={`GEO without offers (${missingGeos.length})`}
        size="lg"
      >
        <div>
          {missingGeos.length === 0 ? (
            <p className="text-sm text-content-subtle text-center py-8">
              All GEOs are covered by offers!
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {missingGeos.map((country) => (
                  <span
                    key={country.code}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-danger-soft text-danger text-sm border border-danger/30"
                  >
                    <span className="font-medium">{country.code}</span>
                    <span className="text-danger">{country.name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-between items-center pt-4 mt-4 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              onClick={copyMissingGeos}
              disabled={missingGeos.length === 0}
            >
              {copiedMissingGeos ? 'Copied!' : 'Copy list'}
            </Button>
            <Button type="button" variant="secondary" onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <TrafficGeoGapsModal
        isOpen={modalMode === 'traffic-geo-gaps'}
        onClose={closeModal}
      />
      <GeoCoverageModal
        isOpen={modalMode === 'geo-coverage'}
        onClose={closeModal}
        offers={offers}
      />
      <PoolCoverageModal
        isOpen={modalMode === 'pool-coverage'}
        onClose={closeModal}
        offers={offers}
        campaigns={poolCampaigns}
      />
    </div>
  );
}

interface OfferFiltersProps {
  filters: Filters;
  onChange: (f: Filters) => void;
  partnerOptions: string[];
  brandOptions: string[];
  trafficSourceOptions: string[];
  activeCount: number;
  totalCount: number;
  filteredCount: number;
  onClear: () => void;
}

function OfferFilters({
  filters,
  onChange,
  partnerOptions,
  brandOptions,
  trafficSourceOptions,
  activeCount,
  totalCount,
  filteredCount,
  onClear,
}: OfferFiltersProps) {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  function toggleCollapsed() {
    setCollapsed((prev) => !prev);
  }

  const selectCls =
    'block w-full px-2.5 py-1.5 border border-border rounded-md text-sm bg-surface focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary';

  const toOption = (s: string) => ({ value: s, label: s });

  return (
    <div className="bg-surface border border-border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={toggleCollapsed}
          className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
          aria-expanded={!collapsed}
        >
          <svg
            className={`w-4 h-4 text-content-subtle flex-shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <h2 className="text-base font-semibold text-content">Filters</h2>
          {activeCount > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
              {activeCount}
            </span>
          )}
        </button>
        <div className="flex items-center gap-3 text-xs text-content-subtle">
          <span>
            {activeCount > 0 ? `${filteredCount} / ${totalCount}` : `${totalCount} offers`}
          </span>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="text-primary hover:underline font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2 lg:col-span-4">
            <label className="block text-xs text-content-subtle mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => onChange({ ...filters, search: e.target.value })}
              placeholder="Search partner, brand, KPI, GEO, URL, contact…"
              className={selectCls}
            />
          </div>

          <div>
            <label className="block text-xs text-content-subtle mb-1">Partner</label>
            <MultiSelect
              value={filters.partners}
              onChange={(v) => onChange({ ...filters, partners: v })}
              options={partnerOptions.map(toOption)}
              searchable
              size="sm"
              placeholder="Any partner"
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">Brand</label>
            <MultiSelect
              value={filters.brands}
              onChange={(v) => onChange({ ...filters, brands: v })}
              options={brandOptions.map(toOption)}
              searchable
              size="sm"
              placeholder="Any brand"
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">Traffic Source</label>
            <MultiSelect
              value={filters.trafficSources}
              onChange={(v) => onChange({ ...filters, trafficSources: v })}
              options={trafficSourceOptions.map(toOption)}
              searchable
              size="sm"
              placeholder="Any source"
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">Vertical</label>
            <MultiSelect
              value={filters.verticals}
              onChange={(v) => onChange({ ...filters, verticals: v })}
              options={VERTICAL_OPTIONS}
              size="sm"
              placeholder="Any vertical"
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">GEO</label>
            <CountryMultiSelect
              value={filters.geoCodes}
              onChange={(codes) => onChange({ ...filters, geoCodes: codes })}
              emptyHint="Any country"
            />
          </div>

          <div>
            <label className="block text-xs text-content-subtle mb-1">Currency</label>
            <MultiSelect
              value={filters.currencies}
              onChange={(v) => onChange({ ...filters, currencies: v })}
              options={[
                { value: 'USD', label: 'USD ($)' },
                { value: 'EUR', label: 'EUR (€)' },
              ]}
              size="sm"
              placeholder="Any currency"
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">Status</label>
            <MultiSelect
              value={filters.statuses}
              onChange={(v) => onChange({ ...filters, statuses: v })}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
              ]}
              size="sm"
              placeholder="Any status"
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">KPI text</label>
            <Select
              value={filters.hasKpi}
              onChange={(v) => onChange({ ...filters, hasKpi: v as Filters['hasKpi'] })}
              options={[
                { value: 'all', label: 'Any' },
                { value: 'yes', label: 'Has KPI' },
                { value: 'no', label: 'No KPI' },
              ]}
              size="sm"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-content-subtle mb-1">Cap state</label>
            <Select
              value={filters.capState}
              onChange={(v) => onChange({ ...filters, capState: v as Filters['capState'] })}
              options={[
                { value: 'all', label: 'Any' },
                { value: 'none', label: 'No cap' },
                { value: 'in_progress', label: 'In progress' },
                { value: 'reached', label: 'Reached' },
              ]}
              size="sm"
              className="w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}
